'use client';

import CabinPhotoNote from '@/components/property/CabinPhotoNote';
import { useState } from 'react';
import Image from '@/components/media/SiteImage';
import PhotoLightbox from '@/components/ui/PhotoLightbox';
import { cn } from '@/lib/utils';
import { GALLERY_ITEMS } from '@/content/gallery-images';

const allItems = GALLERY_ITEMS;
const filters = ['all', 'skycabin', 'levikka', 'varakka', 'iso-yllas', 'pikupoikka', 'isopoikka', 'instagram'] as const;
const defaultFilterLabels: Record<string, string> = {
  all: 'All Photos',
  skycabin: 'Skycabin',
  levikka: 'Levikka',
  varakka: 'Varakka',
  'iso-yllas': 'Iso-Ylläs',
  pikupoikka: 'PikuPoikka',
  isopoikka: 'IsoPoikka',
  mestari: 'Mestari',
  instagram: 'Lapland',
};

interface GalleryLabels {
  filterLabels?: Record<string, string>;
  filterHeading?: string;
  emptyText?: string;
}

export default function GalleryPageClient({ labels }: { labels?: GalleryLabels }) {
  const filterLabels = { ...defaultFilterLabels, ...labels?.filterLabels };
  const filterHeading = labels?.filterHeading ?? 'Browse Photos by Category';
  const emptyText = labels?.emptyText ?? 'No photos found for this filter.';
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const filtered =
    activeFilter === 'all'
      ? allItems
      : allItems.filter((item) => item.source === activeFilter);

  const openLightbox = (idx: number) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);

  const goNext = () => {
    if (lightboxIdx !== null) {
      setLightboxIdx((lightboxIdx + 1) % filtered.length);
    }
  };

  const goPrev = () => {
    if (lightboxIdx !== null) {
      setLightboxIdx(
        (lightboxIdx - 1 + filtered.length) % filtered.length
      );
    }
  };

  const lightboxItem = lightboxIdx !== null ? filtered[lightboxIdx] : null;

  return (
    <>
      {/* Filter Bar */}
      <h2 className="sr-only">{filterHeading}</h2>
      <div className="flex flex-wrap gap-2 mb-10">
        {filters.map((filter) => (
          <button
            key={filter}
            aria-pressed={activeFilter === filter}
            onClick={() => {
              setActiveFilter(filter);
              setLightboxIdx(null);
            }}
            className={cn(
              'min-h-11 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 cursor-pointer',
              activeFilter === filter
                ? 'bg-[#9B7B3A] text-slate-950'
                : 'bg-[#F0EDE5] text-[#5A5A6E] hover:text-[#1A1A2E]'
            )}
          >
            {filterLabels[filter]} (
            {filter === 'all'
              ? allItems.length
              : allItems.filter((i) => i.source === filter).length}
            )
          </button>
        ))}
      </div>

      {/* Masonry Grid */}
      <div className="columns-2 lg:columns-3 gap-2 sm:gap-4">
        {filtered.map((item, idx) => (
          <div key={item.id} className="break-inside-avoid mb-2 sm:mb-4">
            <div className="rounded-xl overflow-hidden bg-white shadow-sm">
              <button
                onClick={() => openLightbox(idx)}
                className="w-full cursor-pointer"
              >
                <div className="relative aspect-[3/2]">
                  <><Image
                    src={item.src}
                    alt={item.alt}
                    width={600}
                    height={400}
                    fill
                    className="object-cover"
                    sizes="(max-width:1024px) 50vw, 33vw"
                    loading="lazy"
                  /><CabinPhotoNote slug={item.source} src={item.src} compact /></>
                </div>
              </button>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="text-center py-12 text-lg text-[#5A5A6E]">
          {emptyText}
        </p>
      )}

      {lightboxItem && lightboxIdx !== null && (
        <PhotoLightbox index={lightboxIdx} count={filtered.length} onClose={closeLightbox} onPrevious={goPrev} onNext={goNext} caption={<><p>{lightboxItem.alt}</p><CabinPhotoNote slug={lightboxItem.source} src={lightboxItem.src} caption light /></>}>
          <Image src={lightboxItem.src} alt={lightboxItem.alt} width={1200} height={800} fill className="object-contain" sizes="100vw" />
          <CabinPhotoNote slug={lightboxItem.source} src={lightboxItem.src} compact />
        </PhotoLightbox>
      )}
    </>
  );
}
